import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { compose } from 'recompose';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import EmulateUser from 'pages/app/Users/gql/mutations/emulate-user';
import { formatPhoneNumber } from 'shared/formatters';
import storage from 'shared/storage';
import DeleteMember from '../../AddMember/gql/DeleteMember';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';
import AddStaffModal from './AddStaffModal';
import LoginAsConfirmationModal from './LoginAsConfirmationModal';
import RecipientsSection from './RecipientsSection';
import {
  StaffListContainer,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  SectionHeader,
  SectionTitle,
  StaffCount,
  StaffCard,
  StaffAvatar,
  StaffInfo,
  StaffName,
  StaffDetails,
  StaffDetail,
  StaffActions,
} from './StyledComponents';

// Wrapper for add button
const AddButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 20px;
  }
`;

const SectionDivider = styled.div`
  margin: 40px 0 32px 0;
  border-top: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgb(226, 232, 240)'};
`;

// Wrapper for action buttons
const ActionButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

// Icons
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const LoginAsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const StaffTab = ({
  members = [],
  payoutRecipients = [],
  accountId,
  setFieldValue,
  deleteMember,
  emulateUser,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLoginAsModal, setShowLoginAsModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Determine route prefix based on current path
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const handleEditCapabilities = (member) => {
    history.push(
      `${routePrefix}/accounts/editAccount/${accountId}/staff/${member.id}/capabilities`
    );
  };

  const handleDeleteClick = (member) => {
    setSelectedMember(member);
    setShowDeleteModal(true);
  };

  const handleLoginAsClick = (member) => {
    setSelectedMember(member);
    setShowLoginAsModal(true);
  };

  const handleLoginAsConfirm = async () => {
    if (!selectedMember) return;

    setIsLoggingIn(true);
    try {
      const response = await emulateUser(selectedMember.id);

      if (!response || response.errors) {
        toast.error('Something went wrong with the emulation.');
      } else {
        const user = response.data.emulateUser;
        storage.set('user', user);
        // Redirect to appropriate dashboard based on user role
        // Staff members don't have access to main admin pages
        const redirectPath =
          user.role === 'employee'
            ? '/admin-employee/home'
            : '/admin-track/home';
        window.location.href = redirectPath;
      }
    } catch (error) {
      toast.error('Something went wrong with the emulation.');
    } finally {
      setIsLoggingIn(false);
      setShowLoginAsModal(false);
      setSelectedMember(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMember) return;

    setIsDeleting(true);
    try {
      const response = await deleteMember({
        user_id: selectedMember.id,
        account_id: parseInt(accountId),
      });

      if (!response || response.errors) {
        toast.error('Error removing staff member');
      } else {
        const newMembers = members.filter(
          (member) => member.id !== selectedMember.id
        );
        setFieldValue('members', newMembers);
        toast.success('Staff member removed successfully');
      }
    } catch (error) {
      toast.error('Error removing staff member');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSelectedMember(null);
    }
  };

  const handleAddSuccess = (newMember) => {
    const newMembers = [...members, newMember];
    setFieldValue('members', newMembers);
    setShowAddModal(false);
    // Navigate to capabilities page for the new member
    history.push(
      `${routePrefix}/accounts/editAccount/${accountId}/staff/${newMember.id}/capabilities`
    );
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>
          Staff Members
          {members.length > 0 && <StaffCount>({members.length})</StaffCount>}
        </SectionTitle>
        <AddButtonWrapper>
          <ButtonV2
            elevated
            type="button"
            onClick={() => setShowAddModal(true)}
            icon={<PlusIcon />}
          >
            Add Staff
          </ButtonV2>
        </AddButtonWrapper>
      </SectionHeader>

      {members.length === 0 ? (
        <Card>
          <EmptyState>
            <EmptyStateIcon>
              <UsersIcon />
            </EmptyStateIcon>
            <EmptyStateTitle>No Staff Members</EmptyStateTitle>
            <EmptyStateText>
              Add staff members to give them access to manage this account.
              Staff can scan tickets, manage events, and more.
            </EmptyStateText>
            <AddButtonWrapper>
              <ButtonV2
                elevated
                type="button"
                onClick={() => setShowAddModal(true)}
                icon={<PlusIcon />}
              >
                Add Your First Staff Member
              </ButtonV2>
            </AddButtonWrapper>
          </EmptyState>
        </Card>
      ) : (
        <StaffListContainer>
          {members.map((member) => (
            <StaffCard key={member.id}>
              <StaffAvatar>{getInitials(member.formatted_name)}</StaffAvatar>
              <StaffInfo>
                <StaffName>{member.formatted_name}</StaffName>
                <StaffDetails>
                  {member.cellphone && (
                    <StaffDetail>
                      <PhoneIcon />
                      {formatPhoneNumber(member.cellphone)}
                    </StaffDetail>
                  )}
                  {member.email && (
                    <StaffDetail>
                      <EmailIcon />
                      {member.email}
                    </StaffDetail>
                  )}
                </StaffDetails>
              </StaffInfo>
              <StaffActions>
                <ActionButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={() => handleEditCapabilities(member)}
                    title="Edit capabilities"
                    icon={<EditIcon />}
                  />
                </ActionButtonWrapper>
                <ActionButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={() => handleLoginAsClick(member)}
                    title="Login as this user"
                    icon={<LoginAsIcon />}
                  />
                </ActionButtonWrapper>
                <ActionButtonWrapper>
                  <ButtonV2
                    danger
                    variant="icon"
                    type="button"
                    onClick={() => handleDeleteClick(member)}
                    title="Remove staff member"
                    icon={<TrashIcon />}
                  />
                </ActionButtonWrapper>
              </StaffActions>
            </StaffCard>
          ))}
        </StaffListContainer>
      )}

      <SectionDivider />

      <RecipientsSection
        payoutRecipients={payoutRecipients}
        setFieldValue={setFieldValue}
        accountId={accountId}
      />

      <AddStaffModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        accountId={accountId}
        onSuccess={handleAddSuccess}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedMember(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={selectedMember?.formatted_name}
        itemType="staff member"
        isDeleting={isDeleting}
      />

      <LoginAsConfirmationModal
        isOpen={showLoginAsModal}
        onClose={() => {
          setShowLoginAsModal(false);
          setSelectedMember(null);
        }}
        onConfirm={handleLoginAsConfirm}
        memberName={selectedMember?.formatted_name}
        isLoggingIn={isLoggingIn}
      />
    </>
  );
};

StaffTab.propTypes = {
  members: PropTypes.array,
  payoutRecipients: PropTypes.array,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setFieldValue: PropTypes.func.isRequired,
  deleteMember: PropTypes.func.isRequired,
  emulateUser: PropTypes.func.isRequired,
};

export default compose(DeleteMember, EmulateUser)(StaffTab);
